import { create } from 'zustand';
import { apiClient } from '../lib/api';

export const useMessageStore = create((set, get) => ({
  // State
  messages: [],
  selectedMessage: null,
  activeFilter: 'all',
  searchQuery: '',
  loading: false,
  error: null,
  pagination: {
    currentPage: 0,
    limit: 10
  },

  // Actions
  setMessages: (messages) => {
    // Ensure messages is always an array
    const messagesArray = Array.isArray(messages) ? messages : (messages?.data || []);
    set({ messages: messagesArray });
  },

  setSelectedMessage: (message) => set({ selectedMessage: message }),

  setActiveFilter: (filter) => set({
    activeFilter: filter,
    pagination: { ...get().pagination, currentPage: 0 }
  }),

  setSearchQuery: (query) => set({
    searchQuery: query,
    pagination: { ...get().pagination, currentPage: 0 }
  }),

  setLoading: (loading) => set({ loading }),

  setError: (error) => set({ error }),

  setPagination: (pagination) => set({ pagination }),

  nextPage: () => set((state) => ({
    pagination: {
      ...state.pagination,
      currentPage: state.pagination.currentPage + 1
    }
  })),

  prevPage: () => set((state) => ({
    pagination: {
      ...state.pagination,
      currentPage: Math.max(0, state.pagination.currentPage - 1)
    }
  })),

  // Async Actions
  loadMessages: async (userProfile = null) => {
    const { pagination, searchQuery, activeFilter } = get();
    set({ loading: true, error: null });

    try {
      // Calculate skip based on current page
      const skip = pagination.currentPage * pagination.limit;

      // Build filters object for API
      const filters = {};

      // Add broker_id filter if user profile exists
      if (userProfile?.userprofile_id) {
        filters.broker_id = userProfile.userprofile_id;
      }

      // Add search filters if search query exists
      if (searchQuery.trim()) {
        filters.subject = searchQuery;
      }

      // Add status filter based on activeFilter
      if (activeFilter === 'unread') {
        filters.processed = false;
      } else if (activeFilter === 'read') {
        filters.processed = true;
      } else if (activeFilter === 'inbound') {
        filters.message_type = 'inbound_email';
      } else if (activeFilter === 'outbound') {
        filters.message_type = 'outbound_email';
      }

      const data = await apiClient.getMessages(skip, pagination.limit, filters);
      // Ensure data is an array before setting
      const messagesArray = Array.isArray(data) ? data : (data?.data || []);
      set({ messages: messagesArray, loading: false });

      // Auto-select first message if no message is selected and list is not empty
      if (messagesArray.length > 0 && !get().selectedMessage) {
        get().viewMessage(messagesArray[0]);
      }
    } catch (err) {
      console.error('Error loading messages:', err);
      set({ error: err.message || 'Failed to load messages', loading: false });
    }
  },

  viewMessage: async (message) => {
    // Show message in right panel
    set({ selectedMessage: message });

    // Mark as read if unread
    if (!message.processed) {
      try {
        await apiClient.markMessageProcessed(message.message_id);

        // Update local state instead of reloading from API
        set((state) => ({
          messages: state.messages.map(msg =>
            msg.message_id === message.message_id
              ? { ...msg, processed: true }
              : msg
          ),
          selectedMessage: { ...message, processed: true }
        }));
      } catch (err) {
        console.error('Error marking message as read:', err);
      }
    }
  },

  // Getters
  getUnreadCount: () => {
    const messages = get().messages;
    // Ensure messages is an array before filtering
    const messagesArray = Array.isArray(messages) ? messages : [];
    return messagesArray.filter((msg) => !msg.processed).length;
  }
}));
